import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Mail, Send, Paperclip, AtSign, Star, Archive, Trash2, Loader2, Plus,
  Search, Inbox as InboxIcon, Users as UsersIcon, X, CornerDownLeft,
} from 'lucide-react';
import { toast } from 'sonner';

// --- Helpers ---
const esc = (s) => (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const autolink = (s) =>
  esc(s).replace(/\bhttps?:\/\/[^\s<]+/g, (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer" class="underline text-emerald-700">${m}</a>`);
const niceTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const initials = (s) => (s || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const BOXES = [
  { key: 'inbox', label: 'Inbox', icon: InboxIcon },
  { key: 'starred', label: 'Starred', icon: Star },
  { key: 'sent', label: 'Sent', icon: Send },
  { key: 'archive', label: 'Archive', icon: Archive },
];

export default function MessagesPage() {
  const [sp] = useSearchParams();
  const { user, has } = useAuth();
  const canSend = has('messages_send');
  const [box, setBox] = useState('inbox');
  const [filter, setFilter] = useState('all'); // all | unread | mail | chats
  const [q, setQ] = useState('');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  // People lookup cache
  const [peopleCache, setPeopleCache] = useState(null);

  // Load list
  const loadThreads = async () => {
    setLoading(true);
    try {
      const res = await api.get('/messages/threads', {
        params: { box, filter, q: q || undefined },
      });
      setThreads(res.data?.threads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [box, filter, q]);

  // Load specific thread
  const loadThread = async (id) => {
    setActiveId(id);
    try {
      const res = await api.get(`/messages/threads/${id}`);
      setThreadDetail(res.data);
      // Refresh unread badges
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load thread');
    }
  };

  // Deep links: ?compose=1&client=... or ?thread=...
  useEffect(() => {
    const t = sp.get('thread');
    if (t) loadThread(t);
    if (sp.get('compose') === '1') setComposeOpen(true);
    // eslint-disable-next-line
  }, []);

  // Poll active thread for new messages every 20 s
  useEffect(() => {
    if (!activeId) return;
    const i = setInterval(() => {
      api.get(`/messages/threads/${activeId}`).then((r) => setThreadDetail(r.data)).catch(() => {});
    }, 20000);
    return () => clearInterval(i);
  }, [activeId]);

  const onFilePick = async (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = '';
    const good = [];
    for (const f of files) {
      if (!/^image\/(png|jpeg|jpg|webp|gif)$/i.test(f.type)) {
        toast.error(`${f.name}: only images up to 2 MB are allowed.`); continue;
      }
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name}: 2 MB max.`); continue;
      }
      const b64 = await fileToBase64(f);
      good.push({ filename: f.name, mime_type: f.type, size: f.size, data_base64: b64 });
    }
    setReplyFiles((p) => [...p, ...good].slice(0, 3));
  };

  const sendReply = async () => {
    if (!activeId || (!replyBody.trim() && replyFiles.length === 0)) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${activeId}/reply`, {
        body: replyBody.trim(),
        attachments: replyFiles,
      });
      setReplyBody(''); setReplyFiles([]);
      await loadThread(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  const star = async (t) => {
    try {
      await api.post(`/messages/threads/${t.id}/star`, null, { params: { starred: !t.starred } });
      loadThreads(); if (activeId === t.id) loadThread(t.id);
    } catch {}
  };
  const archive = async (t) => {
    try {
      await api.post(`/messages/threads/${t.id}/archive`, null, { params: { archived: !t.archived } });
      loadThreads();
    } catch {}
  };

  return (
    <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.3fr)]" data-testid="messages-page">
      {/* ===== LEFT SIDEBAR ===== */}
      <Card className="shine-card p-3 rounded-xl h-fit">
        {canSend && (
          <Button
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => setComposeOpen(true)}
            data-testid="compose-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Compose
          </Button>
        )}
        <div className="mt-3 space-y-0.5">
          {BOXES.map((b) => (
            <button
              key={b.key}
              onClick={() => { setBox(b.key); setActiveId(null); setThreadDetail(null); }}
              data-selectable
              data-state={box === b.key ? 'active' : 'inactive'}
              className={`shine-pill-dark w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left ${
                box === b.key ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`box-${b.key}`}
            >
              <b.icon className="h-4 w-4" /> {b.label}
            </button>
          ))}
        </div>
      </Card>

      {/* ===== THREAD LIST ===== */}
      <Card className="shine-card p-3 rounded-xl min-h-[70vh]">
        <div className="flex items-center gap-2 mb-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input placeholder="Search mail and chat" value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-8" data-testid="messages-search" />
          </div>
        </div>
        <div className="flex items-center gap-1 mb-2 text-xs">
          {['all', 'unread', 'mail', 'chats'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    data-selectable
                    data-state={filter === f ? 'active' : 'inactive'}
                    className={`shine-pill-dark px-2.5 py-1 rounded-full ${
                      filter === f ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`filter-${f}`}>
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : threads.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500 italic">No threads</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => loadThread(t.id)}
                className={`w-full text-left py-2.5 px-2 flex items-start gap-2 rounded ${
                  activeId === t.id ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
                data-testid={`thread-${t.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-700 flex-shrink-0">
                  {initials(t.preview_name || t.last_sender_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className={`truncate text-sm ${t.unread ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
                      {t.last_sender_name || t.preview_name || 'Thread'}
                    </span>
                    {t.unread && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />}
                    <span className="ml-auto text-[11px] text-slate-400 flex-shrink-0">{niceTime(t.updated_at)}</span>
                  </div>
                  <div className={`truncate text-xs ${t.unread ? 'text-slate-800' : 'text-slate-500'}`}>
                    {t.subject || t.name || '(no subject)'}
                  </div>
                  <div className="truncate text-xs text-slate-400 flex items-center gap-1">
                    {t.last_message_preview || '—'}
                    {t.attachment_count > 0 && (
                      <Badge variant="outline" className="text-[10px] py-0 h-4">{t.attachment_count} img</Badge>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* ===== THREAD PANE ===== */}
      <Card className="shine-card p-4 rounded-xl min-h-[70vh]">
        {!threadDetail ? (
          <div className="py-20 text-center text-sm text-slate-400">
            <Mail className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            Select a thread to read it.
          </div>
        ) : (
          <ThreadView
            thread={threadDetail.thread}
            messages={threadDetail.messages}
            people={threadDetail.people}
            me={threadDetail.me}
            replyBody={replyBody}
            setReplyBody={setReplyBody}
            replyFiles={replyFiles}
            setReplyFiles={setReplyFiles}
            onFilePick={onFilePick}
            sending={sending}
            sendReply={sendReply}
            star={() => star({ id: threadDetail.thread.id, starred: false })}
            archive={() => archive({ id: threadDetail.thread.id, archived: false })}
          />
        )}
      </Card>

      {composeOpen && (
        <ComposeDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          onCreated={async (tid) => { setComposeOpen(false); if (tid) await loadThread(tid); loadThreads(); }}
          preClientId={sp.get('client') || null}
        />
      )}
    </div>
  );
}

// ----- Thread view (right pane) -----
function ThreadView({ thread, messages, people, me, replyBody, setReplyBody,
                     replyFiles, setReplyFiles, onFilePick, sending, sendReply,
                     star, archive }) {
  const peopleMap = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-slate-900 text-lg truncate" data-testid="thread-subject">
            {thread.subject || thread.name || '(no subject)'}
          </h2>
          <div className="flex flex-wrap items-center gap-1 mt-1 text-[11px]">
            {thread.client_id && (
              <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Client linked</Badge>
            )}
            <Badge variant="outline">{(thread.participant_ids || []).length} people</Badge>
            <span className="text-slate-400 ml-1">
              {(thread.participant_ids || []).map((pid) => peopleMap[pid]?.name).filter(Boolean).join(', ')}
            </span>
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" onClick={star} title="Star"><Star className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={archive} title="Archive"><Archive className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 py-3">
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} me={me} people={peopleMap} isChat={thread.kind === 'chat'} />
        ))}
      </div>
      <div className="border-t border-slate-100 pt-3">
        <Textarea
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder={thread.kind === 'chat' ? `Message #${thread.name || 'chat'}` : 'Reply… use @ to mention · Ctrl + Enter to send'}
          rows={3}
          onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') sendReply(); }}
          data-testid="reply-input"
        />
        {replyFiles.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {replyFiles.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-xs px-2 py-1 rounded">
                {f.filename} <X className="h-3 w-3 cursor-pointer"
                  onClick={() => setReplyFiles((p) => p.filter((_, idx) => idx !== i))} />
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <label className="icon-btn h-8 w-8 cursor-pointer" title="Add image">
            <Paperclip className="h-4 w-4" />
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFilePick} />
          </label>
          <button type="button"
                  onClick={() => setReplyBody((b) => b + ' @')}
                  className="icon-btn h-8 w-8" title="Mention"
                  data-testid="mention-btn">
            <AtSign className="h-4 w-4" />
          </button>
          <span className="text-[11px] text-slate-400 ml-auto">Ctrl + Enter to send</span>
          <Button onClick={sendReply} disabled={sending} data-testid="send-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m, me, people, isChat }) {
  const isMine = m.sender_id === me;
  const person = people[m.sender_id];
  if (m.deleted) {
    return <div className="text-xs text-slate-400 italic text-center">Message deleted</div>;
  }
  if (isChat) {
    return (
      <div className={`flex gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
        <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
          {initials(m.sender_name)}
        </div>
        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
          isMine ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200'
        }`}>
          <div className="text-[11px] opacity-70 mb-0.5">{m.sender_name}</div>
          <div dangerouslySetInnerHTML={{ __html: autolink(m.body) }} />
          {!!m.attachments?.length && (
            <div className="mt-1 flex flex-wrap gap-1">
              {m.attachments.map((a, i) => (
                <img key={i} src={`data:${a.mime_type};base64,${a.data_base64}`}
                     alt={a.filename}
                     className="rounded max-h-32 border border-black/10"
                     onClick={() => window.open(`data:${a.mime_type};base64,${a.data_base64}`)} />
              ))}
            </div>
          )}
          <div className="text-[10px] opacity-60 mt-0.5">{niceTime(m.created_at)}
            {isMine && m.read_by?.length > 0 && ` · Seen by ${m.read_by.length}`}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">
          {initials(m.sender_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">{m.sender_name}
            <span className="font-normal text-xs text-slate-500"> · {person?.role || m.sender_role}</span>
          </div>
        </div>
        <div className="text-[11px] text-slate-400">{niceTime(m.created_at)}</div>
      </div>
      <div className="text-sm text-slate-700 whitespace-pre-wrap"
           dangerouslySetInnerHTML={{ __html: autolink(m.body) }} />
      {!!m.attachments?.length && (
        <div className="mt-2 flex flex-wrap gap-1">
          {m.attachments.map((a, i) => (
            <img key={i} src={`data:${a.mime_type};base64,${a.data_base64}`}
                 alt={a.filename}
                 className="rounded max-h-40 cursor-pointer border border-slate-200"
                 onClick={() => window.open(`data:${a.mime_type};base64,${a.data_base64}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Compose dialog -----
function ComposeDialog({ open, onOpenChange, onCreated, preClientId }) {
  const [kind] = useState('mail');  // chat creation goes through a different flow (future)
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [toIds, setToIds] = useState([]);  // user ids chosen
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [clientId, setClientId] = useState(preClientId || '');
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get('/messages/people'),
          api.get('/clients'),
        ]);
        setPeople(r1.data?.users || []);
        setClients(r2.data || []);
      } catch {}
    })();
  }, []);

  const filteredPeople = useMemo(() => {
    const q = search.toLowerCase().trim();
    return (people || [])
      .filter((p) => !toIds.includes(p.id))
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q) || (p.role || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [people, search, toIds]);

  const addTo = (id) => { setToIds((p) => [...p, id]); setSearch(''); };
  const removeTo = (id) => setToIds((p) => p.filter((x) => x !== id));

  const onFilePick = async (ev) => {
    const list = Array.from(ev.target.files || []);
    ev.target.value = '';
    const good = [];
    for (const f of list) {
      if (!/^image\/(png|jpeg|jpg|webp|gif)$/i.test(f.type)) {
        toast.error(`${f.name}: images only`); continue;
      }
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name}: 2 MB max`); continue; }
      good.push({ filename: f.name, mime_type: f.type, size: f.size, data_base64: await fileToBase64(f) });
    }
    setFiles((p) => [...p, ...good].slice(0, 3));
  };

  const send = async () => {
    setError('');
    if (toIds.length === 0) return setError('Add at least one recipient.');
    if (!subject.trim()) return setError('Subject is required.');
    if (!body.trim() && files.length === 0) return setError('Say something or attach an image.');
    setSending(true);
    try {
      const res = await api.post('/messages/threads', {
        kind: 'mail',
        subject: subject.trim(),
        participant_ids: toIds,
        client_id: clientId || null,
        body: body.trim(),
        attachments: files,
      });
      toast.success('Message sent');
      onCreated(res.data?.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="compose-dialog">
        <DialogHeader><DialogTitle>Compose</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>To</Label>
            <div className="rounded-md border border-slate-200 bg-white px-2 py-1 min-h-[42px] flex flex-wrap gap-1 items-center">
              {toIds.map((id) => {
                const p = people.find((x) => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2 py-0.5 text-xs">
                    {p?.name || id}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeTo(id)} />
                  </span>
                );
              })}
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder={toIds.length === 0 ? 'Type to search people…' : ''}
                     className="flex-1 min-w-[120px] outline-none text-sm"
                     data-testid="to-input" />
            </div>
            {!!search && filteredPeople.length > 0 && (
              <div className="rounded-md border border-slate-200 mt-1 max-h-40 overflow-auto bg-white shadow">
                {filteredPeople.map((p) => (
                  <button key={p.id} onClick={() => addTo(p.id)}
                          className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-sm flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">
                      {initials(p.name)}
                    </span>
                    {p.name} <span className="text-xs text-slate-500">· {p.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="subject-input" />
          </div>
          <div>
            <Label>Link to client (optional)</Label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    data-testid="client-select">
              <option value="">— None —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.company || c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Message</Label>
            <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)}
                      placeholder="Write your message. Use @name to mention someone."
                      data-testid="body-input" />
            {files.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-xs px-2 py-1 rounded">
                    {f.filename}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} />
                  </span>
                ))}
              </div>
            )}
            <div className="mt-1">
              <label className="text-xs text-emerald-700 cursor-pointer inline-flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Add image (up to 3, 2 MB each)
                <input type="file" accept="image/*" multiple className="hidden" onChange={onFilePick} />
              </label>
            </div>
          </div>
          {error && (
            <div className="text-xs text-red-600 font-medium px-2 py-1 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="compose-send"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
